'use client'

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

const CONSENT_KEY = 'chalk_cookie_consent'

export function CookieConsentBanner() {
  // Start hidden — no SSR render. Show after idle only if no consent stored.
  // This prevents the banner from becoming the LCP element on marketing pages.
  const [visible, setVisible] = useState(false)
  const pathname = usePathname()

  // Sprint 61.56 — suppress banner on /embed paths. Embed iframes live
  // on third-party sites where the host already handles consent; a
  // Chalk-branded banner floating over the iframe is incorrect chrome.
  const isEmbed = pathname?.startsWith('/embed')

  // The authenticated app shell renders a `position:fixed; bottom-0; z-40`
  // mobile bottom nav (BottomTabBar / OrgBottomNav / Level0BottomNav /
  // TrainerBottomTabBar — all `md:hidden`). This banner is also fixed
  // bottom-0 at z-50, so on those routes it sat ON TOP of the bottom nav
  // and the primary tabs could not be tapped while the banner was up.
  // The banner is legitimately app-wide (it describes the sign-in session
  // + last-team preference cookies, which only exist while authenticated),
  // so it must NOT be gated off the app — instead, on the app-shell mobile
  // routes it floats ABOVE the bottom-nav band so both the banner and the
  // tabs remain usable. On desktop the bottom nav is `md:hidden`, so the
  // extra offset is reset at `md` and the banner returns to the edge.
  const APP_SHELL_PREFIXES = [
    '/teams/',
    '/org/',
    '/dashboard',
    '/chat',
    '/profile',
    '/train/',
    '/app/',
  ] as const
  const isAppShell = APP_SHELL_PREFIXES.some(
    (p) => pathname === p || pathname === p.replace(/\/$/, '') || pathname?.startsWith(p),
  )

  useEffect(() => {
    if (isEmbed) return
    if (localStorage.getItem(CONSENT_KEY)) return
    const show = () => setVisible(true)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(show, { timeout: 3000 })
    } else {
      setTimeout(show, 300)
    }
  }, [isEmbed])

  function accept() {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    setVisible(false)
    // Signal consent granted — OneSignal and other SDKs listen for this event
    window.dispatchEvent(new CustomEvent('chalk:cookie-consent', { detail: { accepted: true } }))
  }

  function decline() {
    localStorage.setItem(CONSENT_KEY, 'declined')
    setVisible(false)
    window.dispatchEvent(new CustomEvent('chalk:cookie-consent', { detail: { accepted: false } }))
  }

  if (!visible) return null

  // On app-shell mobile routes, sit ABOVE the fixed bottom nav (its
  // min-h-[56px] row + iOS safe-area inset) so the primary tabs stay
  // tappable. `md:!bottom-0` resets it on desktop where the nav is
  // `md:hidden`. Off the app shell (marketing/auth pages — no bottom nav)
  // the banner keeps its original bottom-edge position + safe-area pad.
  const bottomClass = isAppShell ? 'bottom-[calc(56px+env(safe-area-inset-bottom,0px))] md:!bottom-0' : 'bottom-0'
  const bannerPaddingBottom = isAppShell
    ? '1rem'
    : 'calc(env(safe-area-inset-bottom, 0px) + 1rem)'

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className={`fixed ${bottomClass} left-0 right-0 z-50 p-4 md:p-6`}
      style={{ paddingBottom: bannerPaddingBottom }}
    >
      <div className="max-w-2xl mx-auto rounded-2xl border bg-background shadow-lg p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <p className="text-sm text-muted-foreground flex-1">
          Chalk uses a session cookie to keep you signed in and a preference cookie to remember your last team.
          We do not use advertising or tracking cookies.{' '}
          <Link href="/cookies" className="underline underline-offset-2 hover:text-foreground">
            Cookie Policy
          </Link>
        </p>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={decline}>
            Decline
          </Button>
          <Button size="sm" onClick={accept}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Returns true if the user has accepted cookies (safe to run analytics/push SDKs). */
export function hasCookieConsent(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(CONSENT_KEY) === 'accepted'
}
